import React, { ReactNode } from 'react';
import IModel from '../../../models/model';

import { isDefined } from '../../../utils/helpers';
import { AiFieldVerdictIcon } from './AiFieldVerdict';
import InputTextField, { IInputTextFieldProps } from '../../inputfields/InputTextField';
import { requiredNutritionalFields } from './constants';
import useDebouncedStateString from 'hooks/useDebouncedState';

// TODO move
interface ILabelOptions {
  heading?: string;
  text?: string;
  link?: string;
  required?: boolean;
}

interface IFloatInputProps<T extends IModel> extends Omit<IInputTextFieldProps<T>, 'inputName'> {
  readOnly?: boolean;
  forceRed?: boolean;
  state: T;
  setState?: (state: T) => void;
  name: keyof T;
  required?: boolean;
  dense?: boolean;
  withLabel?: boolean;
  labelOptions?: ILabelOptions;
  label?: string;
  noFullWidth?: boolean;
  disabled?: boolean;
  placeholder?: string;
  style?: React.CSSProperties;
  endAdornment?: ReactNode;
  shrink?: boolean;
  // Number fields sit in a row with their unit, so the failed AI review verdict is an icon with a tooltip;
  // "none" when the field shows the verdict itself below the row.
  aiVerdict?: 'icon' | 'none';
}

export const FloatInput = <T extends IModel>({
  readOnly,
  aiVerdict = 'icon',
  forceRed,
  state,
  setState,
  name,
  required,
  dense = true,
  withLabel,
  labelOptions,
  label,
  noFullWidth,
  disabled,
  placeholder,
  style,
  endAdornment,
  onChange,
  shrink,
  ...props
}: IFloatInputProps<T>) => {
  // TODO any
  const handleChange = (event: any) => {
    const stateName = event.target.name;
    let stateValue = event.target.value;

    if (isDefined(stateValue) && typeof stateValue === 'string') {
      let val = stateValue;
      val = val.trim();
      if (val) {
        stateValue = val.replace(/[^0-9.,]/g, '');
      } else {
        stateValue = val;
      }
    }
    console.log(`NAME: ${stateName} - VALUE: ${stateValue}`);
    setState?.({ ...state, [stateName]: stateValue });

    if (onChange) {
      event.target.value = stateValue;
      onChange(event);
    }
  };
  return (
    <>
      <InputTextField
        hideAiVerdict
        readOnly={readOnly}
        forceRed={forceRed}
        noFullWidth={noFullWidth}
        inputName={name}
        required={required}
        showRed={required && state?.showRed && requiredNutritionalFields.includes((name as any).toString())}
        dense={dense}
        value={state[name]}
        onChange={handleChange}
        disabled={disabled}
        style={{ flex: 0.15, ...style }}
        // type="number"
        withLabel={withLabel}
        label={label}
        labelOptions={labelOptions}
        placeholder={placeholder}
        shrink={shrink}
        endAdornment={endAdornment}
        // inputProps={{pattern: [0-9]}}
        {...props}
      />
      {aiVerdict === 'icon' && <AiFieldVerdictIcon field={name.toString()} />}
    </>
  );
};

interface IFloatInputDebouncedProps<T extends IModel> extends Omit<IFloatInputProps<T>, 'setState'> {
  setState: React.Dispatch<React.SetStateAction<T>>;
}

export const FloatInputDebounced = <T extends IModel>({
  name,
  state,
  setState,
  ...props
}: IFloatInputDebouncedProps<T>) => {
  const [debouncedValue, onDebouncedValueChange] = useDebouncedStateString<T, keyof T>(
    String(state[name] ?? ''),
    setState,
    name
  );

  return (
    <FloatInput<T>
      {...props}
      name={name}
      state={state}
      value={debouncedValue}
      onChange={event => onDebouncedValueChange(event.target.value)}
    />
  );
};

export default FloatInput;
